import { Toaster as Sonner, type ToasterProps } from 'sonner';
import { useIsMobile } from '../../../lib/hooks/useMediaQuery';
import { Icon } from '../../../lib/icon/Icon';
import { cn } from '../../../lib/utils';

const TOAST_BUTTON_CLASS = cn(
  'bg-surface border border-card-border rounded-button',
  'px-4 py-2',
  'text-base text-foreground font-normal leading-body',
  'cursor-pointer justify-self-end col-start-2 mb-3 w-[calc(50%-6px)]',
);

const TOAST_CLOSE_BUTTON_CLASS = cn(
  'absolute top-2 right-2 inline-flex size-6 items-center justify-center',
  'rounded-full text-muted-foreground hover:text-foreground hover:bg-canvas',
  'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
);

const Alert = ({ className, position, ...props }: ToasterProps) => {
  const responsivePosition: ToasterProps['position'] = useIsMobile() ? 'top-center' : 'bottom-center';

  return (
    <Sonner
      theme="light"
      position={position ?? responsivePosition}
      duration={3000}
      className={cn('toaster group', className)}
      icons={{
        success: <Icon name="check-circle" />,
        info: <Icon name="info" />,
        error: <Icon name="triangle-alert" />,
        loading: <Icon name="loader" className="animate-spin" />,
        close: <Icon name="close" size="sm" />,
      }}
      closeButton
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: cn(
            'relative grid grid-cols-[auto_1fr] gap-x-3 overflow-clip rounded-card bg-background border border-card-border shadow-toast pr-9',
            'w-full sm:w-toast sm:max-w-toast',
            'font-body text-base text-foreground',
          ),
          icon: cn('flex items-center justify-center shrink-0 row-span-2 p-2', 'bg-canvas', '[&_svg]:size-6'),
          content: 'flex min-w-0 flex-col gap-1 py-3 col-start-2',
          title: 'font-semibold leading-body',
          description: 'font-normal leading-body',
          actionButton: TOAST_BUTTON_CLASS,
          cancelButton: TOAST_BUTTON_CLASS,
          closeButton: TOAST_CLOSE_BUTTON_CLASS,
          success: '[&_[data-icon]]:bg-success-subtle [&_[data-icon]]:text-success-on-subtle [&_[data-close-button]]:hidden',
          error: '[&_[data-icon]]:bg-error-subtle [&_[data-icon]]:text-error-on-subtle',
          info: '[&_[data-icon]]:bg-canvas',
        },
      }}
      {...props}
    />
  );
};

export { Alert };
